import { useState, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Plane } from 'lucide-react';
import FlightTrackingContext from '../../../../context/tracking/FlightTrackingContext';
import TrackingBanner from '../Shared/TrackingBanner';
import TrackingSearchBox from '../Shared/TrackingSearchBox';
import FlightImg from '../../../../assets/Background/flight_bg.png';
import UpcomingFlightsTable from './sections/UpcomingFlightsTable';
import FlightTrackingResults from './sections/FlightTrackingResults';

const FlightTrackingPage = ({ hideHeader = false }) => {
  const { trackFlight, flightData, loading, error, clearFlightData } = useContext(FlightTrackingContext);
  const [searchParams] = useSearchParams();
  const [flightNumber, setFlightNumber] = useState(searchParams.get('flight_number') || '');
  const [date] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const paramsFlight = searchParams.get('flight_number');
    if (paramsFlight) {
      setFlightNumber(paramsFlight);
      trackFlight(paramsFlight, date);
    }
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (flightNumber) {
      trackFlight(flightNumber, date);
    }
  };

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <TrackingBanner
          title="Live Flight Tracking"
          description="Real-time flight status updates for you and your customers. Track any flight instantly with PNR or Flight Number."
          image={FlightImg}
        />
      )}

      {/* Search Section */}
      <TrackingSearchBox
        fields={[
          {
            label: 'Flight Number',
            placeholder: 'e.g. AI101 or 6E656',
            value: flightNumber,
            onChange: (e) => setFlightNumber(e.target.value.toUpperCase()),
            icon: Plane
          }
        ]}
        onSubmit={handleSearch}
        buttonText="Track"
        loading={loading}
        disabled={!flightNumber.trim()}
      />

      {error && (
        <div className="bg-red-50 border border-red-100 text-red-600 p-6 rounded-3xl flex items-center gap-4 animate-shake">
          <AlertCircle className="w-6 h-6 flex-shrink-0" />
          <p className="font-bold">{error}</p>
        </div>
      )}

      {/* Live Tracking Result Card Section */}
      {flightData && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                clearFlightData();
                setFlightNumber('');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer active:scale-95 shadow-2xs"
            >
              <ArrowLeft size={14} /> Back to Upcoming Flights
            </button>
            <span className="text-xs text-slate-400 font-medium">Viewing live tracking details</span>
          </div>
          <FlightTrackingResults
            flightData={flightData}
            onClose={() => {
              clearFlightData();
              setFlightNumber('');
            }}
          />
        </div>
      )}

      {/* Upcoming Flights Table Section */}
      {!flightData && !loading && <UpcomingFlightsTable />}
    </div>
  );
};

export default FlightTrackingPage;
